import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaCheck, FaTimes, FaEdit, FaEye } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import stateCityData from "../../../data/states.json";

const PendingVoice = () => {
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAd, setselectedAd] = useState(null);
  const [modal, setModal] = useState({ open: false, action: null, adId: null });
  const [rejectReason, setRejectReason] = useState("");
  const [showApproveSpinner, setShowApproveSpinner] = useState(false);
  const [showRejectSpinner, setShowRejectSpinner] = useState(false);
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);
  const [showModifySpinner, setShowModifySpinner] = useState(false);
  const navigate = useNavigate();
  const [refetchTrigger, setRefetchTrigger] = useState(false);
  const [showOverview, setShowOverview] = useState(false);
  const [overviewData, setOverviewData] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  // Update cities when states change
  useEffect(() => {
    if (selectedStates.length > 0) {
      // Collect all cities from selected states
      let allCities = [];
      selectedStates.forEach((state) => {
        allCities = [...allCities, ...(stateCityData[state] || [])];
      });
      setAvailableCities([...new Set(allCities)]);
    } else {
      setAvailableCities([]);
      setSelectedCities([]);
    }
  }, [selectedStates]);

  useEffect(() => {
    const fetchVoices = async () => {
      setLoading(true);
      setError(null);
      try {

        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/admin/get/new/ryv/ads`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log("Fetched Voices Data:", response);
        // Ensure voices is always an array (handle new API structure)
        if (response.data.data) {
          setVoices(response.data.data);
        } else if (Array.isArray(response.data)) {
          setVoices(response.data);
        } else if (response.data && Array.isArray(response.data.voices)) {
          setVoices(response.data.voices);
        } else {
          setVoices([]);
        }
      } catch (err) {
        setError("Failed to fetch voices");
      } finally {
        setLoading(false);
      }
    };
    fetchVoices();
  }, [refetchTrigger]);

  // Filter voices with status 'pending' or 'under review'
  const filteredVoices = voices.filter((voice) => voice.status === "pending" || voice.status === "under review");

  // Pagination logic
  const totalPages = Math.ceil(filteredVoices.length / rowsPerPage);
  const paginatedVoices = filteredVoices.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Handle approve voice
  const handleApprove = async (id) => {
    setShowApproveSpinner(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/admin/approve/ryv/ad/${id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      console.log("Approved Data Response:", response);
      if (response.data && response.data.success) {
        toast.success("Voice approved successfully");
        setTimeout(() => {
          setShowApproveSpinner(false);
          navigate("/admin/approvedvoice");
          window.location.reload();
        }, 1500);
      } else {
        setShowApproveSpinner(false);
        toast.error(response.data.message || "Failed to approve voice");
      }
    } catch (error) {
      setShowApproveSpinner(false);
      toast.error("Failed to approve voice");
    }
  };

  // Handle reject voice
  const handleReject = async (id, reason) => {
    setShowRejectSpinner(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/admin/reject/ryv/ad/${id}`,
        { rejectionNote: reason },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      console.log("Rejected Data Response:", response);
      if (response.data && response.data.success) {
        toast.success(response.data.message || "Voice rejected successfully");
        setTimeout(() => {
          setShowRejectSpinner(false);
          window.location.reload();
        }, 1500);
      } else {
        setShowRejectSpinner(false);
        toast.error(response.data.message || "Failed to reject voice");
      }
    } catch (error) {
      setShowRejectSpinner(false);
      toast.error("Failed to reject voice");
    }
  };

  // Handle overview button click
  const handleOverview = (voice) => {
    setOverviewData(voice);
    setShowOverview(true);
  };

  // Handle modify voice
  const handleModify = async (id, note) => {
    setShowModifySpinner(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/admin/modify/ryv/ad/${id}`,
        {
          adminSelectState: selectedStates,
          adminSelectCities: selectedCities,
          note: note || ""
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      console.log("Modify Data Response:", response);
      if (response.data && response.data.success) {
        toast.success(response.data.message || "Voice modified successfully");
        setTimeout(() => {
          setShowModifySpinner(false);
          window.location.reload();
        }, 1500);
      } else {
        setShowModifySpinner(false);
        toast.error(response.data.message || "Failed to modify voice");
      }
    } catch (error) {
      setShowModifySpinner(false);
      toast.error("Failed to modify voice");
    }
  };

  // Modal confirm handler
  const handleModalConfirm = () => {
    if (modal.action === "approve") {
      handleApprove(modal.adId);
    } else if (modal.action === "reject") {
      handleReject(modal.adId, rejectReason);
    } else if (modal.action === "modify") {
      handleModify(modal.adId, rejectReason);
    }
    setModal({ open: false, action: null, adId: null });
    setRejectReason("");
    setSelectedStates([]);
    setSelectedCities([]);
  };

  // Modal cancel handler
  const handleModalCancel = () => {
    setModal({ open: false, action: null, adId: null });
    setRejectReason("");
    setSelectedStates([]);
    setSelectedCities([]);
  };



  if (loading) return <div>Loading...</div>;

  return (
    <>
      {showApproveSpinner && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="flex flex-col items-center p-8 bg-white rounded-2xl shadow-2xl border border-blue-200 animate-fadeIn">
            {/* Custom Tailwind Spinner */}
            <div className="relative mb-4 w-16 h-16 grid place-items-center">
              {/* Outer ring */}
              <span
                className="absolute inset-0 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin"
                style={{ borderWidth: "4px" }}
              />
              {/* Middle ring */}
              <span
                className="absolute inset-0 m-1 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin-slow"
                style={{ borderWidth: "4px", animationDuration: "2s" }}
              />
              {/* Inner ring */}
              <span
                className="absolute inset-0 m-2 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin-slower"
                style={{ borderWidth: "4px", animationDuration: "3s" }}
              />
            </div>
            <span className="text-lg font-semibold text-blue-700 tracking-wide">
              Processing Approval...
            </span>
            <span className="text-sm text-gray-500 mt-2">
              Please wait while we update the status.
            </span>
          </div>
        </div>
      )}
      {showRejectSpinner && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="flex flex-col items-center p-8 bg-white rounded-2xl shadow-2xl border border-blue-200 animate-fadeIn">
            {/* Custom Tailwind Spinner */}
            <div className="relative mb-4 w-16 h-16 grid place-items-center">
              {/* Outer ring */}
              <span
                className="absolute inset-0 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin"
                style={{ borderWidth: "4px" }}
              />
              {/* Middle ring */}
              <span
                className="absolute inset-0 m-1 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin-slow"
                style={{ borderWidth: "4px", animationDuration: "2s" }}
              />
              {/* Inner ring */}
              <span
                className="absolute inset-0 m-2 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin-slower"
                style={{ borderWidth: "4px", animationDuration: "3s" }}
              />
            </div>
            <span className="text-lg font-semibold text-red-700 tracking-wide">
              Processing Rejection...
            </span>
            <span className="text-sm text-gray-500 mt-2">
              Please wait while we update the status.
            </span>
          </div>
        </div>
      )}
      {showModifySpinner && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="flex flex-col items-center p-8 bg-white rounded-2xl shadow-2xl border border-purple-200 animate-fadeIn">
            {/* Custom Tailwind Spinner */}
            <div className="relative mb-4 w-16 h-16 grid place-items-center">
              {/* Outer ring */}
              <span
                className="absolute inset-0 rounded-full border-4 border-transparent border-r-[#8b5cf6] animate-spin"
                style={{ borderWidth: "4px" }}
              />
              {/* Middle ring */}
              <span
                className="absolute inset-0 m-1 rounded-full border-4 border-transparent border-r-[#8b5cf6] animate-spin-slow"
                style={{ borderWidth: "4px", animationDuration: "2s" }}
              />
              {/* Inner ring */}
              <span
                className="absolute inset-0 m-2 rounded-full border-4 border-transparent border-r-[#8b5cf6] animate-spin-slower"
                style={{ borderWidth: "4px", animationDuration: "3s" }}
              />
            </div>
            <span className="text-lg font-semibold text-purple-700 tracking-wide">
              Processing Modification...
            </span>
            <span className="text-sm text-gray-500 mt-2">
              Please wait while we update the targeting.
            </span>
          </div>
        </div>
      )}
      {error && <div className="text-red-500 mb-4">{error}</div>}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-2xl font-bold text-gray-800 tracking-tight">Pending Voices</h2>
        <div className="flex flex-wrap gap-2 sm:space-x-2 sm:space-y-0">
          <button
            className="flex-1 sm:flex-none px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 font-semibold transition-colors text-sm sm:text-base shadow"
            onClick={() => navigate("/admin/approvedvoice")}
          >
            Approved
          </button>
          <button
            className="flex-1 sm:flex-none px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 font-semibold transition-colors text-sm sm:text-base shadow"
            onClick={() => navigate("/admin/rejectedvoice")}
          >
            Rejected
          </button>
          <button
            className="flex-1 sm:flex-none px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 font-semibold transition-colors text-sm sm:text-base shadow"
            onClick={() => navigate("/admin/runningvoice")}
          >
            Accepted By Reporter
          </button>
          <button
            className="flex-1 sm:flex-none px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-semibold transition-colors text-sm sm:text-base shadow"
            onClick={() => navigate("/admin/runningvoice")}
          >
            Running
          </button>

        </div>
      </div>
      {/* Responsive Table/Card List */}
      <div className="w-full">
        {/* Desktop Table */}
        <div className="hidden lg:block">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-2 md:p-6 animate-fadeIn w-full overflow-x-auto">
            <table className="min-w-full text-sm md:text-base">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left font-semibold text-gray-700 whitespace-nowrap sticky top-0 z-10 bg-gray-100">Media</th>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left font-semibold text-gray-700 whitespace-nowrap sticky top-0 z-10 bg-gray-100">RYV ID</th>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left font-semibold text-gray-700 whitespace-nowrap sticky top-0 z-10 bg-gray-100">User ID</th>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left font-semibold text-gray-700 whitespace-nowrap sticky top-0 z-10 bg-gray-100">State</th>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left font-semibold text-gray-700 whitespace-nowrap sticky top-0 z-10 bg-gray-100">City</th>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left font-semibold text-gray-700 whitespace-nowrap sticky top-0 z-10 bg-gray-100">Residence Address</th>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left font-semibold text-gray-700 whitespace-nowrap sticky top-0 z-10 bg-gray-100">Description</th>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left font-semibold text-gray-700 whitespace-nowrap sticky top-0 z-10 bg-gray-100">Target Audience</th>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left font-semibold text-gray-700 whitespace-nowrap sticky top-0 z-10 bg-gray-100">Created At</th>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left font-semibold text-gray-700 whitespace-nowrap sticky top-0 z-10 bg-gray-100">Status</th>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left font-semibold text-gray-700 whitespace-nowrap sticky top-0 z-10 bg-gray-100">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredVoices.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="text-center py-6 text-gray-400 text-base">
                      No pending voices found.
                    </td>
                  </tr>
                ) : (
                  paginatedVoices.map((voice, idx) => (
                    <tr key={voice._id} className={idx % 2 === 0 ? "bg-white hover:bg-blue-50 transition" : "bg-gray-50 hover:bg-blue-50 transition"}>
                      <td className="px-2 md:px-4 py-2 md:py-3 border-b border-gray-100 text-center min-w-[180px]">
                        {voice.image && (
                          <img src={voice.image.startsWith('http') ? voice.image : `${process.env.REACT_APP_API_BASE_URL}/${voice.image.replace(/\\/g, '/')}`} alt="media" className="w-20 h-20 md:w-20 md:h-20 object-cover rounded mx-auto" />
                        )}
                        {voice.video && (
                          <video src={voice.video.startsWith('http') ? voice.video : `${process.env.REACT_APP_API_BASE_URL}/${voice.video.replace(/\\/g, '/')}`} className="w-20 h-20 md:w-20 md:h-20 object-cover rounded mx-auto" />
                        )}
                      </td>
                      <td className="px-2 md:px-4 py-2 md:py-3 border-b border-gray-100 text-center max-w-[120px] md:max-w-xs">
                        <span className="block truncate break-all text-blue-700 font-mono cursor-pointer select-all" title={voice._id}>{voice._id}</span>
                      </td>
                      <td className="px-2 md:px-4 py-2 md:py-3 border-b border-gray-100 text-center max-w-[100px] md:max-w-xs">
                        <span className="block truncate break-all text-gray-800 font-mono cursor-pointer select-all" title={voice.userId || 'N/A'}>{voice.userId || "N/A"}</span>
                      </td>
                      <td className="px-2 md:px-4 py-2 md:py-3 border-b border-gray-100 text-center">{voice.state || "N/A"}</td>
                      <td className="px-2 md:px-4 py-2 md:py-3 border-b border-gray-100 text-center">{voice.city || "N/A"}</td>
                      <td className="px-2 md:px-4 py-2 md:py-3 border-b border-gray-100 text-center break-words max-w-[120px] md:max-w-xs">{voice.residenceAddress || voice.address || "N/A"}</td>
                      <td className="px-2 md:px-4 py-2 md:py-3 border-b border-gray-100 text-center break-words max-w-[120px] md:max-w-xs">{voice.description || "N/A"}</td>
                      <td className="px-2 md:px-4 py-2 md:py-3 border-b border-gray-100 text-center">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${voice.targetUserType === "reporter"
                            ? "bg-blue-100 text-blue-800"
                            : voice.targetUserType === "influencer"
                              ? "bg-purple-100 text-purple-800"
                              : voice.targetUserType === "both"
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-800"
                          }`}>
                          {voice.targetUserType === "reporter" ? "Reporters Only" :
                            voice.targetUserType === "influencer" ? "Influencers Only" :
                              voice.targetUserType === "both" ? "Both" : "Not Specified"}
                        </span>
                      </td>
                      <td className="px-2 md:px-4 py-2 md:py-3 border-b border-gray-100 text-center">
                        {(() => {
                          const d = new Date(voice.createdAt);
                          const date = d.toLocaleDateString("en-GB");
                          const time = d.toLocaleTimeString("en-GB", {
                            hour12: true,
                          });
                          return `${date} ${time}`;
                        })()}
                      </td>
                      <td className="px-2 md:px-4 py-2 md:py-3 border-b border-gray-100 text-center">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${voice.status === "approved"
                              ? "bg-green-100 text-green-800"
                              : voice.status === "rejected"
                                ? "bg-red-100 text-red-800"
                                : "bg-yellow-100 text-yellow-800"
                            }`}
                        >
                          {voice.status === "pending" || voice.status === "under review" ? "Under Review" : voice.status}
                        </span>
                      </td>
                      <td className="px-2 md:px-4 py-2 md:py-3 border-b border-gray-100 align-top">
                        <div className="flex flex-col gap-2 min-w-[80px]">
                          <button
                            className="px-2 md:px-3 py-1 bg-green-500 text-white rounded-md hover:bg-green-600 transition font-semibold shadow text-xs md:text-sm"
                            onClick={() => handleApprove(voice._id)}
                          >
                            Approve
                          </button>
                          <button
                            className="px-2 md:px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-600 transition font-semibold shadow text-xs md:text-sm"
                            onClick={() => {
                              setModal({ open: true, action: "reject", adId: voice._id });
                            }}
                          >
                            Reject
                          </button>
                          <button
                            className="px-2 md:px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition font-semibold shadow text-xs md:text-sm flex items-center justify-center"
                            onClick={() => handleOverview(voice)}
                          >
                            <FaEye className="mr-1" /> Overview
                          </button>
                          <button
                            className="px-2 md:px-3 py-1 bg-purple-500 text-white rounded-md hover:bg-purple-600 transition font-semibold shadow text-xs md:text-sm flex items-center justify-center"
                            onClick={() => {
                              navigate("/admin/raise-your-voice/modify", {
                                state: {
                                  voiceId: voice._id,
                                  voiceData: voice
                                }
                              });
                            }}
                          >
                            <FaEdit className="mr-1" /> Modify
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {/* Pagination Controls - Desktop */}
            {totalPages > 1 && (
              <div className="flex justify-center mt-6">
                <nav className="inline-flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-4 py-1 rounded border border-yellow-400 text-yellow-800 bg-white hover:bg-yellow-100 transition-colors ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    Previous
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-1 rounded border border-yellow-400 text-yellow-800 bg-white hover:bg-yellow-100 transition-colors font-semibold ${currentPage === page ? 'bg-yellow-300 text-yellow-900 font-bold' : ''}`}
                      aria-current={currentPage === page ? 'page' : undefined}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`px-4 py-1 rounded border border-yellow-400 text-yellow-800 bg-white hover:bg-yellow-100 transition-colors ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    Next
                  </button>
                </nav>
              </div>
            )}
          </div>
        </div>
        {/* Mobile Table (always tabular) */}
        <div className="block lg:hidden w-full overflow-x-auto">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-2 animate-fadeIn w-full">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-2 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Media</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Ad ID</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">User ID</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">State</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">City</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Residence Address</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Description</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Target</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Created At</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Status</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredVoices.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="text-center py-6 text-gray-400 text-base">No pending voices found.</td>
                  </tr>
                ) : (
                  paginatedVoices.map((voice, idx) => (
                    <tr key={voice._id} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                      <td className="px-2 py-2 align-center">
                        {voice.image && (
                          <img src={voice.image.startsWith('http') ? voice.image : `${process.env.REACT_APP_API_BASE_URL}/${voice.image.replace(/\\/g, '/')}`} alt="media" className="w-14 h-14 object-cover rounded mx-auto" />
                        )}
                        {voice.video && (
                          <video src={voice.video.startsWith('http') ? voice.video : `${process.env.REACT_APP_API_BASE_URL}/${voice.video.replace(/\\/g, '/')}`} className="w-14 h-14 object-cover rounded mx-auto" />
                        )}
                      </td>
                      <td className="px-2 py-2 align-top max-w-[80px]">
                        <span className="block truncate break-all text-blue-700 font-mono select-all" title={voice._id}>{voice._id}</span>
                      </td>
                      <td className="px-2 py-2 align-top max-w-[60px]">
                        <span className="block truncate break-all text-gray-800 font-mono select-all" title={voice.userId || 'N/A'}>{voice.userId || "N/A"}</span>
                      </td>
                      <td className="px-2 py-2 align-top">{voice.state || "N/A"}</td>
                      <td className="px-2 py-2 align-top">{voice.city || "N/A"}</td>
                      <td className="px-2 py-2 align-top break-words max-w-[80px]">{voice.residenceAddress || voice.address || "N/A"}</td>
                      <td className="px-2 py-2 align-top break-words max-w-[80px]">{voice.description || "N/A"}</td>
                      <td className="px-2 py-2 align-top">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${voice.targetUserType === "reporter"
                            ? "bg-blue-100 text-blue-800"
                            : voice.targetUserType === "influencer"
                              ? "bg-purple-100 text-purple-800"
                              : voice.targetUserType === "both"
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-800"
                          }`}>
                          {voice.targetUserType === "reporter" ? "Reporter" :
                            voice.targetUserType === "influencer" ? "Influencer" :
                              voice.targetUserType === "both" ? "Both" : "N/A"}
                        </span>
                      </td>
                      <td className="px-2 py-2 align-top whitespace-nowrap">{voice.createdAt ? new Date(voice.createdAt).toLocaleString() : "N/A"}</td>
                      <td className="px-2 py-2 align-top">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${voice.status === "approved"
                              ? "bg-green-100 text-green-800"
                              : voice.status === "rejected"
                                ? "bg-red-100 text-red-800"
                                : "bg-yellow-100 text-yellow-800"
                            }`}
                        >
                          {voice.status === "pending" || voice.status === "under review" ? "Under Review" : voice.status}
                        </span>
                      </td>
                      <td className="px-2 py-2 align-top">
                        <div className="flex flex-col gap-1 min-w-[60px]">

                          <button
                            className="px-2 py-1 bg-green-500 text-white rounded-md hover:bg-green-600 transition font-semibold shadow text-xs"
                            onClick={() => handleApprove(voice._id)}
                          >
                            Approve
                          </button>
                          <button
                            className="px-2 py-1 bg-red-500 text-white rounded-md hover:bg-red-600 transition font-semibold shadow text-xs"
                            onClick={() => {
                              setModal({ open: true, action: "reject", adId: voice._id });
                            }}
                          >
                            Reject
                          </button>
                          <button
                            className="px-2 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition font-semibold shadow text-xs flex items-center justify-center"
                            onClick={() => handleOverview(voice)}
                          >
                            <FaEye className="mr-1" /> Overview
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {/* Pagination Controls - Mobile */}
            {totalPages > 1 && (
              <div className="flex justify-center mt-6">
                <nav className="inline-flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-4 py-1 rounded border border-yellow-400 text-yellow-800 bg-white hover:bg-yellow-100 transition-colors ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    Previous
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-1 rounded border border-yellow-400 text-yellow-800 bg-white hover:bg-yellow-100 transition-colors font-semibold ${currentPage === page ? 'bg-yellow-300 text-yellow-900 font-bold' : ''}`}
                      aria-current={currentPage === page ? 'page' : undefined}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`px-4 py-1 rounded border border-yellow-400 text-yellow-800 bg-white hover:bg-yellow-100 transition-colors ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    Next
                  </button>
                </nav>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Popup Modal for Voice Details and Actions */}
      {selectedAd && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50 transition-all">
          <div className="bg-white rounded-2xl shadow-2xl p-4 sm:p-6 md:p-10 w-full max-w-xs sm:max-w-md md:max-w-2xl text-left relative animate-fadeIn border border-blue-200 overflow-y-auto max-h-[90vh]">
            <button
              className="absolute top-2 right-3 text-gray-400 hover:text-gray-700 text-3xl font-bold"
              onClick={() => setselectedAd(null)}
              aria-label="Close"
            >
              &times;
            </button>
            <div className="flex flex-col md:flex-row items-center mb-6 md:mb-8">
              <div className="relative">
                {selectedAd.image && (
                  <>
                    <img
                      src={selectedAd.image.startsWith('http') ? selectedAd.image : `${process.env.REACT_APP_API_BASE_URL}/${selectedAd.image.replace(/\\/g, '/')}`}
                      alt={selectedAd.name || 'Voice Media'}
                      className="w-40 h-40 sm:w-52 sm:h-52 md:w-60 md:h-60 object-cover rounded-xl border mb-2 md:mb-0 shadow"
                      style={{ maxWidth: "100%", maxHeight: "260px" }}
                    />
                    <a
                      href={selectedAd.image.startsWith('http') ? selectedAd.image : `${process.env.REACT_APP_API_BASE_URL}/${selectedAd.image.replace(/\\/g, '/')}`}
                      download
                      className="absolute top-2 right-2 bg-white bg-opacity-80 rounded-full p-2 shadow hover:bg-blue-100 transition"
                      title="Download Image"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-5 h-5 text-blue-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
                        />
                      </svg>
                    </a>
                  </>
                )}
                {selectedAd.video && (
                  <>
                    <video
                      src={selectedAd.video.startsWith('http') ? selectedAd.video : `${process.env.REACT_APP_API_BASE_URL}/${selectedAd.video.replace(/\\/g, '/')}`}
                      controls
                      className="w-40 h-40 sm:w-52 sm:h-52 md:w-60 md:h-60 object-cover rounded-xl border mb-2 md:mb-0 shadow"
                      style={{ maxWidth: "100%", maxHeight: "260px" }}
                    />
                    <a
                      href={selectedAd.video.startsWith('http') ? selectedAd.video : `${process.env.REACT_APP_API_BASE_URL}/${selectedAd.video.replace(/\\/g, '/')}`}
                      download
                      className="absolute top-2 right-2 bg-white bg-opacity-80 rounded-full p-2 shadow hover:bg-blue-100 transition"
                      title="Download Video"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-5 h-5 text-blue-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
                        />
                      </svg>
                    </a>
                  </>
                )}
              </div>
              <div className="w-full md:ml-8">
                <h2 className="text-lg sm:text-xl md:text-2xl font-bold mb-2">
                  {selectedAd.name || selectedAd.title || "Voice Details"}
                </h2>
                <span
                  className={`inline-block px-4 py-1 rounded-full text-xs sm:text-sm font-semibold ${selectedAd.status === "approved"
                      ? "bg-green-100 text-green-800"
                      : selectedAd.status === "rejected"
                        ? "bg-red-100 text-red-800"
                        : "bg-yellow-100 text-yellow-800"
                    }`}
                >
                  {selectedAd.status === "pending" || selectedAd.status === "under review"
                    ? "Under Review"
                    : selectedAd.status}
                </span>
              </div>
            </div>
            <div className="mb-4 md:mb-6 grid grid-cols-1 sm:grid-cols-2 gap-x-4 md:gap-x-8 gap-y-2 md:gap-y-3 text-sm sm:text-base">
              <div>
                <span className="font-medium">Ad ID:</span> {selectedAd._id}
              </div>
              <div>
                <span className="font-medium">User ID:</span> {selectedAd.userId || "N/A"}
              </div>
              <div>
                <span className="font-medium">State:</span> {selectedAd.state || "N/A"}
              </div>
              <div>
                <span className="font-medium">City:</span> {selectedAd.city || "N/A"}
              </div>
              <div>
                <span className="font-medium">Residence Address:</span> {selectedAd.residenceAddress || selectedAd.address || "N/A"}
              </div>
              <div>
                <span className="font-medium">Description:</span> {selectedAd.description || "N/A"}
              </div>
              <div>
                <span className="font-medium">Created At:</span> {selectedAd.createdAt ? new Date(selectedAd.createdAt).toLocaleString() : "N/A"}
              </div>
              <div>
                <span className="font-medium">Status:</span> {(selectedAd.status === "pending" || selectedAd.status === "under review") ? "Under Review" : selectedAd.status || "N/A"}
              </div>
            </div>
            {/* Show buttons only if voice is pending */}
            {selectedAd.status === "pending" && (
              <div className="flex flex-col sm:flex-row justify-between gap-3 mt-6 md:mt-8">
                <button
                  onClick={() => {
                    setModal({
                      open: true,
                      action: "approve",
                      adId: selectedAd._id,
                    });
                    setselectedAd(null);
                  }}
                  className="flex items-center justify-center px-4 py-2 sm:px-6 sm:py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-semibold shadow w-full sm:w-auto"
                >
                  <FaCheck className="w-5 h-5 mr-2" />
                  Approve
                </button>
                <button
                  onClick={() => {
                    setModal({
                      open: true,
                      action: "reject",
                      adId: selectedAd._id,
                    });
                    setselectedAd(null);
                  }}
                  className="flex items-center justify-center px-4 py-2 sm:px-6 sm:py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-semibold shadow w-full sm:w-auto"
                >
                  <FaTimes className="w-5 h-5 mr-2" />
                  Reject
                </button>
                {/* <button
                  onClick={() => {
                    handleModifyVoice(selectedAd._id);
                    setselectedAd(null);
                  }}
                  className="flex items-center justify-center px-4 py-2 sm:px-6 sm:py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors font-semibold shadow w-full sm:w-auto"
                >
                  <FaEdit className="w-5 h-5 mr-2" />
                  Modify
                </button> */}
              </div>
            )}
          </div>
        </div>
      )}
      {/* Modal for confirm action */}
      {modal.open && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 transition-all">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-[90%] max-w-4xl max-h-[90vh] text-center relative animate-fadeIn overflow-y-auto">
            <div className="flex justify-center mb-4">
              {modal.action === "approve" ? (
                <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-100">
                  <FaCheck className="text-green-600 text-3xl" />
                </span>
              ) : modal.action === "reject" ? (
                <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-100">
                  <FaTimes className="text-red-600 text-3xl" />
                </span>
              ) : modal.action === "modify" ? (
                <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-purple-100">
                  <FaEdit className="text-purple-600 text-3xl" />
                </span>
              ) : null}
            </div>
            <h2 className="text-xl font-bold mb-2">
              {modal.action === "approve"
                ? "Approve Voice"
                : modal.action === "reject"
                  ? "Reject Voice"
                  : modal.action === "modify"
                    ? "Modify Voice"
                    : ""}
            </h2>
            <p className="mb-6 text-gray-700">
              {modal.action === "approve"
                ? "Are you sure you want to approve this voice?"
                : modal.action === "reject"
                  ? "Are you sure you want to reject this voice?"
                  : modal.action === "modify"
                    ? "Modify this voice and select target states for reporters/influencers?"
                    : ""}
            </p>
            {modal.action === "reject" && (
              <div className="mb-6 text-left">
                <label
                  htmlFor="rejectReason"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Reason for Rejection:
                </label>
                <textarea
                  id="rejectReason"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
                  rows={3}
                  placeholder="Enter reason for rejection..."
                />
              </div>
            )}

            {modal.action === "modify" && (
              <div className="mb-6 text-left">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                  <h3 className="text-lg font-semibold text-purple-800 mb-2">Modify Voice Targeting</h3>
                  <p className="text-sm text-purple-700">
                    Select states and cities to target specific reporters and influencers for this voice post.
                  </p>
                </div>

                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select States for Reporter/Influencer Matching:
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-40 overflow-y-auto border border-gray-300 rounded-lg p-3">
                  {[
                    "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
                    "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
                    "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
                    "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
                    "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
                    "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu",
                    "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry"
                  ].map((state) => (
                    <label key={state} className="flex items-center text-sm p-1 hover:bg-gray-50 rounded">
                      <input
                        type="checkbox"
                        name="selectedStates"
                        value={state}
                        checked={selectedStates.includes(state)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedStates([...selectedStates, state]);
                          } else {
                            setSelectedStates(selectedStates.filter(s => s !== state));
                          }
                        }}
                        className="mr-2 w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <span className="text-xs">{state}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Select states where reporters/influencers should be notified about this voice.
                </p>
                {selectedStates.length > 0 && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                    <p className="text-xs text-blue-700">
                      <strong>Selected States:</strong> {selectedStates.join(", ")}
                    </p>
                  </div>
                )}

                {/* City Selection */}
                {availableCities.length > 0 && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Cities (Optional):
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-40 overflow-y-auto border border-gray-300 rounded-lg p-3">
                      {availableCities.map((city) => (
                        <label key={city} className="flex items-center text-sm p-1 hover:bg-gray-50 rounded">
                          <input
                            type="checkbox"
                            name="selectedCities"
                            value={city}
                            checked={selectedCities.includes(city)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedCities([...selectedCities, city]);
                              } else {
                                setSelectedCities(selectedCities.filter(c => c !== city));
                              }
                            }}
                            className="mr-2 w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                          />
                          <span className="text-xs">{city}</span>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Select specific cities within the chosen states. Leave empty to target all cities in selected states.
                    </p>
                    {selectedCities.length > 0 && (
                      <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded">
                        <p className="text-xs text-green-700">
                          <strong>Selected Cities:</strong> {selectedCities.join(", ")}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <label
                  htmlFor="modifyNote"
                  className="block text-sm font-medium text-gray-700 mb-2 mt-4"
                >
                  Admin Note (Optional):
                </label>
                <textarea
                  id="modifyNote"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none text-sm"
                  rows={3}
                  placeholder="Enter admin note..."
                />
              </div>
            )}
            <div className="flex justify-center gap-4 mt-6">
              <button
                onClick={handleModalConfirm}
                className={`px-6 py-3 rounded-lg font-semibold shadow transition-colors text-white ${modal.action === "approve"
                    ? "bg-green-500 hover:bg-green-600"
                    : modal.action === "reject"
                      ? "bg-red-500 hover:bg-red-600"
                      : modal.action === "modify"
                        ? "bg-purple-500 hover:bg-purple-600"
                        : "bg-gray-500 hover:bg-gray-600"
                  }`}
                disabled={modal.action === "reject" && !rejectReason.trim() || modal.action === "modify" && selectedStates.length === 0}
              >
                {modal.action === "modify" ? "Modify Voice" : "Yes"}
              </button>
              <button
                onClick={handleModalCancel}
                className="px-6 py-3 rounded-lg font-semibold bg-gray-200 text-gray-700 hover:bg-gray-300 shadow transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Popup Modal for Overview */}
      {showOverview && overviewData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto relative">
            {/* Header Section */}
            <div className="bg-blue-600 text-white p-4 rounded-t-lg sticky top-0 z-10">
              <button
                className="absolute top-3 right-3 text-white hover:text-gray-200 text-xl font-bold"
                onClick={() => setShowOverview(false)}
                title="Close"
              >
                &times;
              </button>
              <h3 className="text-lg font-bold text-center">Voice Details</h3>
            </div>

            <div className="p-6">
              {/* Media Section */}
              <div className="mb-8 pb-6 border-b border-gray-200">
                <h4 className="text-md font-bold text-gray-700 uppercase mb-4 tracking-wide flex items-center">
                  <span className="inline-block w-2 h-2 bg-blue-600 rounded-full mr-2"></span>
                  Media
                </h4>
                <div className="flex justify-center">
                  {overviewData.image && (
                    <div className="relative group">
                      <img
                        src={overviewData.image.startsWith('http') ? overviewData.image : `${process.env.REACT_APP_API_BASE_URL}/${overviewData.image.replace(/\\/g, '/')}`}
                        alt="Voice media"
                        className="max-h-60 object-contain rounded-lg shadow-md border border-gray-200 transition-transform hover:scale-[1.02]"
                      />
                      <a
                        href={overviewData.image.startsWith('http') ? overviewData.image : `${process.env.REACT_APP_API_BASE_URL}/${overviewData.image.replace(/\\/g, '/')}`}
                        download
                        className="absolute top-2 right-2 bg-white bg-opacity-80 rounded-full p-2 shadow hover:bg-blue-100 transition opacity-0 group-hover:opacity-100"
                        title="Download Image"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-5 h-5 text-blue-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
                          />
                        </svg>
                      </a>
                    </div>
                  )}
                  {overviewData.video && (
                    <div className="relative group">
                      <video
                        src={overviewData.video.startsWith('http') ? overviewData.video : `${process.env.REACT_APP_API_BASE_URL}/${overviewData.video.replace(/\\/g, '/')}`}
                        controls
                        className="max-h-60 object-contain rounded-lg shadow-md border border-gray-200"
                      />
                      <a
                        href={overviewData.video.startsWith('http') ? overviewData.video : `${process.env.REACT_APP_API_BASE_URL}/${overviewData.video.replace(/\\/g, '/')}`}
                        download
                        className="absolute top-2 right-2 bg-white bg-opacity-80 rounded-full p-2 shadow hover:bg-blue-100 transition opacity-0 group-hover:opacity-100"
                        title="Download Video"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-5 h-5 text-blue-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
                          />
                        </svg>
                      </a>
                    </div>
                  )}
                  {!overviewData.image && !overviewData.video && (
                    <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 text-gray-400 italic text-center">
                      No media available
                    </div>
                  )}
                </div>
              </div>

              {/* Basic Information Section */}
              <div className="mb-8 pb-6 border-b border-gray-200">
                <h4 className="text-md font-bold text-gray-700 uppercase mb-4 tracking-wide flex items-center">
                  <span className="inline-block w-2 h-2 bg-purple-600 rounded-full mr-2"></span>
                  Basic Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-3 rounded-md border border-gray-100 shadow-sm hover:shadow-md transition">
                    <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Name</span>
                    <span className="font-medium text-gray-900">{overviewData.name || "N/A"}</span>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-md border border-gray-100 shadow-sm hover:shadow-md transition">
                    <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Email</span>
                    <span className="font-medium text-gray-900 break-all">{overviewData.email || "N/A"}</span>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-md border border-gray-100 shadow-sm hover:shadow-md transition">
                    <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Phone Number</span>
                    <span className="font-medium text-gray-900">{overviewData.phoneNo || "N/A"}</span>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-md border border-gray-100 shadow-sm hover:shadow-md transition">
                    <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Gender</span>
                    <span className="font-medium text-gray-900 capitalize">{overviewData.gender || "N/A"}</span>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-md border border-gray-100 shadow-sm hover:shadow-md transition">
                    <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Date of Birth</span>
                    <span className="font-medium text-gray-900">{overviewData.dateOfBirth || "N/A"}</span>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-md border border-gray-100 shadow-sm hover:shadow-md transition">
                    <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">User ID</span>
                    <span className="font-medium text-gray-900 break-all text-xs">{overviewData.userId || "N/A"}</span>
                  </div>
                </div>
              </div>

              {/* Location Information Section */}
              <div className="mb-8 pb-6 border-b border-gray-200">
                <h4 className="text-md font-bold text-gray-700 uppercase mb-4 tracking-wide flex items-center">
                  <span className="inline-block w-2 h-2 bg-green-600 rounded-full mr-2"></span>
                  Location Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-3 rounded-md border border-gray-100 shadow-sm hover:shadow-md transition">
                    <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">State</span>
                    <span className="font-medium text-gray-900">{overviewData.state || "N/A"}</span>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-md border border-gray-100 shadow-sm hover:shadow-md transition">
                    <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">City</span>
                    <span className="font-medium text-gray-900">{overviewData.city || "N/A"}</span>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-md border border-gray-100 shadow-sm hover:shadow-md transition">
                    <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Residence Address</span>
                    <span className="font-medium text-gray-900">{overviewData.residenceAddress || "N/A"}</span>
                  </div>
                </div>
              </div>

              {/* ID Information Section */}
              <div className="mb-8 pb-6 border-b border-gray-200">
                <h4 className="text-md font-bold text-gray-700 uppercase mb-4 tracking-wide flex items-center">
                  <span className="inline-block w-2 h-2 bg-yellow-600 rounded-full mr-2"></span>
                  Identification Details
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-3 rounded-md border border-gray-100 shadow-sm hover:shadow-md transition">
                    <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Aadhar Number</span>
                    <span className="font-medium text-gray-900">{overviewData.aadharNo || "N/A"}</span>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-md border border-gray-100 shadow-sm hover:shadow-md transition">
                    <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">PAN Card</span>
                    <span className="font-medium text-gray-900">{overviewData.pancard || "N/A"}</span>
                  </div>
                </div>
              </div>

              {/* Voice Details Section */}
              <div className="mb-8 pb-6 border-b border-gray-200">
                <h4 className="text-md font-bold text-gray-700 uppercase mb-4 tracking-wide flex items-center">
                  <span className="inline-block w-2 h-2 bg-red-600 rounded-full mr-2"></span>
                  Voice Information
                </h4>
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-md border border-gray-100 shadow-sm hover:shadow-md transition">
                    <span className="text-xs font-semibold text-gray-500 uppercase block mb-2">Description</span>
                    <p className="text-gray-900">
                      {overviewData.description || "No description available"}
                    </p>
                  </div>

                  {overviewData.rejectionNote && (
                    <div className="bg-gray-50 p-4 rounded-md border border-gray-100 shadow-sm hover:shadow-md transition">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-2">Rejection Note</span>
                      <p className="text-gray-900">
                        {overviewData.rejectionNote}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Status Information Section */}
              <div className="mb-8">
                <h4 className="text-md font-bold text-gray-700 uppercase mb-4 tracking-wide flex items-center">
                  <span className="inline-block w-2 h-2 bg-blue-600 rounded-full mr-2"></span>
                  Status Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-3 rounded-md border border-gray-100 shadow-sm hover:shadow-md transition">
                    <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Status</span>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${overviewData.status === "approved"
                        ? "bg-green-100 text-green-800"
                        : overviewData.status === "rejected"
                          ? "bg-red-100 text-red-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}>
                      {overviewData.status === "pending" || overviewData.status === "under review"
                        ? "Under Review"
                        : overviewData.status}
                    </span>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-md border border-gray-100 shadow-sm hover:shadow-md transition">
                    <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Created At</span>
                    <span className="font-medium text-gray-900">
                      {overviewData.createdAt
                        ? new Date(overviewData.createdAt).toLocaleString()
                        : "Not available"}
                    </span>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-md border border-gray-100 shadow-sm hover:shadow-md transition">
                    <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Updated At</span>
                    <span className="font-medium text-gray-900">
                      {overviewData.updatedAt
                        ? new Date(overviewData.updatedAt).toLocaleString()
                        : "Not available"}
                    </span>
                  </div>
                </div>
              </div>


              {/* Action Buttons */}
              <div className="mt-8 flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3">
                {overviewData.status === "pending" || overviewData.status === "under review" ? (
                  <>
                    <button
                      onClick={() => {
                        setModal({
                          open: true,
                          action: "approve",
                          adId: overviewData._id,
                        });
                        setShowOverview(false);
                      }}
                      className="flex items-center justify-center px-5 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors font-semibold shadow"
                    >
                      <FaCheck className="mr-2" />
                      Approve
                    </button>
                    <button
                      onClick={() => {
                        setModal({
                          open: true,
                          action: "reject",
                          adId: overviewData._id,
                        });
                        setShowOverview(false);
                      }}
                      className="flex items-center justify-center px-5 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors font-semibold shadow"
                    >
                      <FaTimes className="mr-2" />
                      Reject
                    </button>
                  </>
                ) : null}
                <button
                  className="flex items-center justify-center px-5 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-md transition-colors font-semibold shadow"
                  onClick={() => setShowOverview(false)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
};

export default PendingVoice;
